import { Spacer, Text } from "@hackthenorth/north";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import React from "react";
import styled from "styled-components";

import { Modal } from "src/shared/components";
import { BalanceChip } from "src/views/goose-games/components/balanceChip";
import { TPrizeCheckout } from "src/views/goose-games/types";

import FooterActions from "../../../../footerActions";

import { TABLE_HEADERS } from "./constants";

export interface CheckoutModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  currentBalance: number;
  items: Array<TPrizeCheckout>;
}
const CheckoutModal: React.FC<CheckoutModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  currentBalance,
  items,
}) => {
  const handleConfirm = async () => {
    try {
      onConfirm();
    } catch (error) {
      console.error("Error adjusting points:", error);
    }
  };

  const getBalance = (curr_balance: number, items: Array<TPrizeCheckout>) => {
    return items.reduce((total, item) => {
      const item_total = item.pointCost * item.quantity;
      return total - item_total;
    }, curr_balance);
  };

  return (
    <Modal isOpen={isOpen} height="90vh" scroll>
      <HeaderContainer>
        <Text mods="h3 bold">Checkout Summary</Text>
      </HeaderContainer>
      <Wrapper>
        <TableWrapper>
          <TableHeader>
            {TABLE_HEADERS.map((header, index) => (
              <HeaderCell
                key={header.id}
                alignment={
                  index === 0 ? "left" : index === 1 ? "center" : "right"
                }
              >
                {header.label}
              </HeaderCell>
            ))}
          </TableHeader>
          <TableRows>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell alignment="left">
                  <Text mods="body">{item.name}</Text>
                </TableCell>
                <TableCell alignment="center">
                  <CellQuantity>
                    <Text mods="white">{item.quantity}</Text>
                  </CellQuantity>
                </TableCell>
                <TableCell alignment="right">
                  <BalanceChip points={item.pointCost} />
                </TableCell>
              </TableRow>
            ))}
          </TableRows>
        </TableWrapper>
        <TotalWrapper>
          <Text mods="body bold">Total</Text>
          <BalanceChip
            points={items.reduce(
              (total, item) => total + item.pointCost * item.quantity,
              0
            )}
          />
        </TotalWrapper>
        <BalanceContainer>
          <BalanceWrapper currentBalance={true}>
            <Text mods="body bold">Current balance</Text>
            <BalanceChip points={currentBalance} />
          </BalanceWrapper>
          <BalanceWrapper>
            <Text mods="body bold">New balance after transaction</Text>
            <BalanceChip points={getBalance(currentBalance, items)} />
          </BalanceWrapper>
        </BalanceContainer>
        <WarningWrapper>
          <WarningIcon>
            <ExclamationCircleIcon color="#D97706" width="18px" />
          </WarningIcon>
          <Text
            mods="body textWarning"
            style={{ whiteSpace: "pre-line", lineHeight: "1.5" }}
          >
            Before proceeding, please confirm that we have sufficient stock for
            the above items.
          </Text>
        </WarningWrapper>
      </Wrapper>

      <Spacer height={26} />
      <FooterActions
        confirmText={"Confirm"}
        onConfirm={handleConfirm}
        onCancel={onCancel}
      />
    </Modal>
  );
};

export default CheckoutModal;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 24px;
  gap: 8px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  padding: 16px 24px;
  background-color: ${({ theme }) =>
    theme.globalConstants.color.backgroundGray};
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px 8px 0 0;
`;

const HeaderCell = styled(Text)<{ alignment: string }>`
  color: #6b7280;
  font-family: "Satoshi Variable", sans-serif;
  font-size: 12px !important;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-align: ${({ alignment }) => alignment};
`;

const TableRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-top: none;
  padding: 8px 0;
  border-radius: 0 0 8px 8px;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  width: 100%;
  padding: 12px 24px;
`;

const TableCell = styled.div<{ alignment: string }>`
  color: #4b5563;
  font-family: "Satoshi Variable", sans-serif;
  font-size: 14px !important;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-align: ${({ alignment }) => alignment};
  display: flex;
  align-items: center;
  justify-content: ${({ alignment }) =>
    alignment === "left"
      ? "flex-start"
      : alignment === "center"
      ? "center"
      : "flex-end"};
`;

const CellQuantity = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #6b7280;
`;

const TotalWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 24px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  margin-bottom: 12px;
`;

const BalanceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const BalanceWrapper = styled.div<{ currentBalance?: boolean }>`
  background-color: ${({ currentBalance, theme }) =>
    currentBalance
      ? theme.globalConstants.color.backgroundGray
      : theme.globalConstants.color.backgroundPrimaryLight};
  padding: 12px 24px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const WarningWrapper = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  align-items: start;
  background-color: ${({ theme }) =>
    theme.globalConstants.color.backgroundWarningLight};
  margin-top: 16px;
`;

const WarningIcon = styled.div`
  padding: 4px 0;
`;
